import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import TextInput from "../form/TextInput";
import { Button } from "../ui/button";
import BackButton from "../buttons/BackButton";
import { useNavigate } from "react-router-dom";
import { LandingPageTab } from "@/components/auth/LandingPage";
import { forgetPassword } from "@/api/auth";
import { toast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface Props {
  setTab: Dispatch<SetStateAction<LandingPageTab>>;
}
function ForgotPasswordForm({ setTab }: Props) {
  const [isSent, setIsSent] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown > 0) {
      const timer = window.setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [countdown]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Call API to send reset email
    try {
      setIsSending(true);
      await forgetPassword({ email });
      setIsSent(true);
      setCountdown(20); // start 20s countdown
      toast({
        title: "Reset email sent",
        description: "Please check your email for reset instructions.",
      });
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const [email, setEmail] = useState<string>("");
  return (
    <div className="w-full h-full flex flex-col gap-4 items-center justify-center">
      <div className="flex flex-col gap-2">
        <div className="tracking-wide font-extrabold text-red-800 text-4xl text-center mt-2 mb-2">
          Xorex@HR
        </div>
        <div className="text-2xl font-bold text-center">Forgot Password?</div>
        <div className="text-gray-500 text-center">
          Enter your email and we'll send you reset instructions.
        </div>
      </div>
      <form
        onSubmit={handleSubmit}
        className=" mt-10 w-full h-max flex flex-col gap-6 items-center max-w-[400px]"
      >
        <TextInput
          label="Email"
          placeholder="Enter your email address"
          type="email"
          value={email}
          setValue={setEmail}
        />
        <Button
          type="button"
          variant="link"
          className="self-end underline text-gray-500 hover:text-black"
          onClick={() => setTab(LandingPageTab.LOGIN)}
        >
          Back to Login
        </Button>
        <Button
          type="submit"
          className="w-full"
          disabled={email.length === 0 || isSending || countdown > 0}
        >
          {isSending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : countdown > 0 ? (
            `Please wait ${countdown}s before requesting again...`
          ) : (
            "Send Reset Email"
          )}
        </Button>
      </form>
    </div>
  );
}

export default ForgotPasswordForm;
